// Traitements des outils Routes (docs/routes/README.md §8, lots B et D) : types lus et écrits par les RPC
// `*_route_tool_*_v1` et la fonction Edge `route-optimization`, libellés et calculs d'affichage.

export type RouteToolKind = 'OPTIMIZATION' | 'STREET_SNAP' | 'AI_ASSIST'
export type RouteToolMode = 'ORDER' | 'BALANCE' | 'FLAGS'
export type RouteToolRunStatus =
  'RUNNING' | 'REVIEW' | 'APPLIED' | 'PARTIALLY_APPLIED' | 'FAILED' | 'CANCELLED' | 'REVERTED'

export interface RouteToolUsage {
  provider?: string | undefined
  calls?: number | undefined
  billable?: boolean | undefined
  shipments?: number | undefined
  vehicles?: number | undefined
}

export interface RouteToolRunListItem {
  id: string
  tool: RouteToolKind
  mode: RouteToolMode
  status: RouteToolRunStatus
  season: string
  routeIds: string[]
  routeNames: string[]
  createdAt: string
  createdByName: string | null
  proposalCount: number
  appliedCount: number
  errorCode: string | null
  usage: RouteToolUsage | null
}

export interface RouteMetricsView {
  stops: number
  travelSeconds: number
  distanceMeters: number
  visitSeconds: number
  totalSeconds: number
}

export interface RouteToolProposal {
  routeId: string
  routeName: string
  routeColor: string
  beforeIds: string[]
  afterIds: string[]
  metrics: { before: RouteMetricsView; after: RouteMetricsView } | null
  status: 'PROPOSED' | 'APPLIED' | 'CONFLICT' | 'REVERTED'
  /** La route a changé depuis le calcul : l'appliquer sera refusé. */
  changedSinceRun: boolean
}

export interface RouteToolExcluded {
  locationId: string
  routeId: string
  reason: string
}

export interface RouteToolMoved {
  locationId: string
  fromRouteId: string
  toRouteId: string
}

export interface RouteToolSummary {
  excluded: RouteToolExcluded[]
  moved: RouteToolMoved[]
  totals: {
    before: { travelSeconds: number; distanceMeters: number; totalSeconds: number }
    after: { travelSeconds: number; distanceMeters: number; totalSeconds: number }
  } | null
  balanceTargetSeconds: number | null
  /** Résidences non affectées incluses au lancement, et combien ont été placées. */
  unassignedIncluded: number
  unassignedPlaced: number
}

/** Conflit d'une route (optimisation) ou d'une résidence (drapeaux). */
export interface RouteToolConflict {
  routeId?: string | undefined
  locationId?: string | undefined
  reason: string
}

export interface RouteToolApplication {
  id: string
  appliedAt: string
  report: { applied: string[]; conflicts: RouteToolConflict[] }
  revertedAt: string | null
  revertReport: { restored: string[]; conflicts: RouteToolConflict[] } | null
}

export interface RouteToolRunDetail {
  id: string
  tool: RouteToolKind
  mode: RouteToolMode
  status: RouteToolRunStatus
  season: string
  parameters: {
    serviceMinutes?: number | undefined
    lockedLocationIds?: string[] | undefined
    depot?: { lat: number; lng: number } | null | undefined
    includeUnassigned?: boolean | undefined
    /** Rapprocher les drapeaux : recul depuis l'axe (m). */
    setbackMeters?: number | undefined
    method?: FlagMethod | 'GEMINI_VISION' | undefined
    /** Drapeaux IA : modèle Gemini utilisé. */
    model?: string | undefined
    objective?: OptimizationObjective | undefined
    travelDurationMultiple?: number | undefined
    avoidHighways?: boolean | undefined
    balance?: BalanceStrength | null | undefined
  }
  summary: RouteToolSummary | null
  usage: RouteToolUsage | null
  errorCode: string | null
  createdAt: string
  completedAt: string | null
  createdByName: string | null
  proposals: RouteToolProposal[]
  /** Outil « Rapprocher les drapeaux » : une proposition par résidence. */
  locationProposals: FlagProposal[]
  applications: RouteToolApplication[]
}

export interface FlagPoint {
  latitude: number | null
  longitude: number | null
  source: 'GEOMETRY' | 'MANUAL' | 'AI' | 'MAPBOX' | null
  version: number
}

export interface FlagProposal {
  locationId: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  latitude: number | null
  longitude: number | null
  before: FlagPoint
  proposedLatitude: number | null
  proposedLongitude: number | null
  abstainReason: string | null
  warnings: string[]
  details: {
    streetName?: string | undefined
    roadClass?: string | null | undefined
    axisDistance?: number | undefined
    setback?: number | undefined
    moveDistance?: number | undefined
  } | null
  status: 'PROPOSED' | 'ABSTAINED' | 'PROTECTED' | 'APPLIED' | 'CONFLICT' | 'REVERTED'
  changedSinceRun: boolean
}

export interface FlagRunLocation {
  id: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  latitude: number | null
  longitude: number | null
  flagSource: string | null
  flagVersion: number
}

export interface CreateFlagRunCommand {
  idempotencyKey: string
  season: string
  routeIds: string[]
  includeUnassigned: boolean
  parameters: {
    /** GEOMETRY : calcul AQréseau ; MAPBOX_ACCESS_POINT : point d'accès du géocodage Mapbox (essai). */
    method: FlagMethod
    setbackMeters: number
    maxSearchMeters: number
    overwriteManual: boolean
  }
}

export type FlagMethod = 'GEOMETRY' | 'MAPBOX_ACCESS_POINT'

export interface CompleteFlagRunCommand {
  runId: string
  proposals: (
    | { locationId: string; latitude: number; longitude: number; warnings: string[]; details: object }
    | { locationId: string; abstainReason: string; warnings: string[]; details: object }
  )[]
  summary: object
}

export interface ApplyFlagsCommand {
  idempotencyKey: string
  runId: string
  locationIds: string[]
}

export interface StreetSegmentsStatus {
  count: number
  sourceVersion: string | null
  sourceDate: string | null
}

export interface OptimizeRoutesCommand {
  idempotencyKey: string
  season: string
  mode: RouteToolMode
  routeIds: string[]
  /** Mode BALANCE : adresses qui restent dans leur route. */
  lockedLocationIds: string[]
  serviceMinutes: number
  depot: { lat: number; lng: number } | null
  /** Inclure les résidences non affectées de la saison : le service peut les placer dans les routes choisies. */
  includeUnassigned: boolean
  objective: OptimizationObjective
  /** Temps de trajet × ce facteur (tracteurs plus lents qu'une voiture), de 0,5 à 10. */
  travelDurationMultiple: number
  avoidHighways: boolean
  /** Répartition seulement : force de l'équilibrage des durées. */
  balance: BalanceStrength
}

export type OptimizationObjective = 'DISTANCE' | 'TIME'
export type BalanceStrength = 'NONE' | 'LIGHT' | 'STRONG'

/** Réglages avancés par défaut, pensés pour des tracteurs (demande du 12 octobre 2026). */
export const tractorOptimizationDefaults = {
  objective: 'DISTANCE',
  travelDurationMultiple: 2,
  avoidHighways: true,
  balance: 'LIGHT',
} as const satisfies Pick<
  OptimizeRoutesCommand,
  'objective' | 'travelDurationMultiple' | 'avoidHighways' | 'balance'
>

export const objectiveLabels: Record<OptimizationObjective, string> = {
  DISTANCE: 'distance la plus courte',
  TIME: 'temps de trajet le plus court',
}

export const balanceLabels: Record<BalanceStrength, string> = {
  NONE: 'aucun équilibrage',
  LIGHT: 'équilibrage léger',
  STRONG: 'équilibrage fort',
}

export interface OptimizeRoutesResult {
  runId: string
  status: RouteToolRunStatus
  error: string | null
}

export interface ApplyRouteToolCommand {
  idempotencyKey: string
  runId: string
  routeIds: string[]
}

export interface ApplyRouteToolResult {
  status: 'APPLIED' | 'PARTIAL' | 'CONFLICT'
  applicationId: string | null
  applied: string[]
  conflicts: RouteToolConflict[]
}

export interface RevertRouteToolCommand {
  idempotencyKey: string
  applicationId: string
}

export interface RevertRouteToolResult {
  status: 'REVERTED' | 'PARTIAL' | 'CONFLICT'
  restored: string[]
  conflicts: RouteToolConflict[]
}

// ---------------------------------------------------------------- Libellés

export const routeToolRunStatusLabels: Record<RouteToolRunStatus, string> = {
  RUNNING: 'En cours',
  REVIEW: 'À vérifier',
  APPLIED: 'Appliqué',
  PARTIALLY_APPLIED: 'Partiellement appliqué',
  FAILED: 'Échoué',
  CANCELLED: 'Abandonné',
  REVERTED: 'Annulé',
}

export const routeToolLabels: Record<RouteToolKind, string> = {
  OPTIMIZATION: 'Optimisation des routes',
  STREET_SNAP: 'Rapprochement géométrique',
  AI_ASSIST: 'Assistance IA',
}

export const routeToolModeLabels: Record<RouteToolMode, string> = {
  ORDER: 'Ordre de chaque route',
  BALANCE: 'Répartition entre routes',
  FLAGS: 'Drapeaux par résidence',
}

const errorTexts: Record<string, string> = {
  PROVIDER_NOT_CONFIGURED: 'Le service d’optimisation n’est pas configuré.',
  PROVIDER_TIMEOUT: 'Le service d’optimisation n’a pas répondu à temps.',
  PROVIDER_AUTH: 'Le service d’optimisation a refusé l’accès (compte de service ou rôle).',
  PROVIDER_QUOTA: 'Quota du service d’optimisation atteint. Réessayez plus tard.',
  PROVIDER_REJECTED: 'Le service d’optimisation a refusé la demande.',
  PROVIDER_ERROR: 'Le service d’optimisation est en erreur.',
  INVALID_RESULT: 'Le résultat reçu était incohérent : il a été écarté sans rien modifier.',
  NO_LOCATED_STOPS: 'Aucune adresse localisée dans ce périmètre.',
  TOO_MANY_STOPS: 'Trop d’adresses pour un seul calcul (1 000 au plus).',
  INVALID_COORDINATES: 'Le point de départ et de retour est invalide.',
  ROUTE_TOOL_ALREADY_RUNNING: 'Un calcul est déjà en cours sur ces routes.',
  ROUTE_UPDATE_FORBIDDEN: 'Vous n’avez pas le droit de lancer cet outil.',
  ROUTE_NOT_FOUND: 'Une route du périmètre n’existe plus.',
  ROUTE_TOOL_RUN_NOT_APPLICABLE: 'Ce résultat ne peut plus être appliqué.',
  ROUTE_TOOL_SELECTION_INCOMPLETE: 'Une répartition s’applique sur toutes ses routes à la fois.',
  ROUTE_TOOL_APPLICATION_ALREADY_REVERTED: 'Ce lot est déjà annulé.',
}

export function routeToolErrorText(code: string | null | undefined): string {
  if (!code) return 'Le calcul a échoué.'
  return errorTexts[code] ?? `Le calcul a échoué (${code}).`
}

const excludedTexts: Record<string, string> = {
  UNLOCATED: 'Adresse à localiser : gardée à la fin de sa route',
  NO_VEHICLE: 'Aucune route ne peut la desservir : gardée à la fin de sa route',
}

/** `routeId` vide : résidence non affectée incluse, qui le reste. */
export function excludedReasonText(reason: string, routeId = 'route'): string {
  if (routeId === '') {
    return reason === 'UNLOCATED'
      ? 'Non affectée à localiser : reste non affectée'
      : `Non placée par le service (${reason}) : reste non affectée`
  }
  return excludedTexts[reason] ?? `Écartée par le service (${reason}) : gardée à la fin de sa route`
}

// ---------------------------------------------------------------- Calculs d'affichage

/** « 1 h 05 », « 12 min », « 45 s ». */
export function formatDuration(seconds: number): string {
  const rounded = Math.round(seconds)
  if (rounded < 60) return `${String(rounded)} s`
  const minutes = Math.round(rounded / 60)
  if (minutes < 60) return `${String(minutes)} min`
  return `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')}`
}

/** « 850 m », « 12,4 km ». */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${String(Math.round(meters))} m`
  return `${(meters / 1000).toLocaleString('fr-CA', { maximumFractionDigits: 1 })} km`
}

/** Écart signé « −12 min » / « +3 min » / « = ». */
export function formatDurationDelta(beforeSeconds: number, afterSeconds: number): string {
  const delta = afterSeconds - beforeSeconds
  if (Math.abs(delta) < 30) return '='
  return `${delta < 0 ? '−' : '+'}${formatDuration(Math.abs(delta))}`
}

export interface ProposalChanges {
  /** Adresses arrivées d'une autre route. */
  movedIn: string[]
  /** Adresses parties vers une autre route. */
  movedOut: string[]
  /** Adresses restées dans la route mais à une autre place. */
  reordered: number
}

export function proposalChanges(
  proposal: Pick<RouteToolProposal, 'beforeIds' | 'afterIds'>,
): ProposalChanges {
  const before = new Set(proposal.beforeIds)
  const after = new Set(proposal.afterIds)
  const stayedBefore = proposal.beforeIds.filter((id) => after.has(id))
  const stayedAfter = proposal.afterIds.filter((id) => before.has(id))
  return {
    movedIn: proposal.afterIds.filter((id) => !before.has(id)),
    movedOut: proposal.beforeIds.filter((id) => !after.has(id)),
    reordered: stayedAfter.filter((id, index) => stayedBefore[index] !== id).length,
  }
}

/** Le résultat peut-il encore être appliqué (en tout ou en partie) ? */
export function isApplicable(status: RouteToolRunStatus): boolean {
  return status === 'REVIEW' || status === 'PARTIALLY_APPLIED'
}

/** Routes sélectionnables à l'application : en répartition, toutes ensemble ; sinon celles non encore appliquées. */
export function applicableRouteIds(detail: Pick<RouteToolRunDetail, 'mode' | 'proposals'>): string[] {
  return detail.proposals
    .filter((proposal) => proposal.status === 'PROPOSED' || proposal.status === 'CONFLICT')
    .map((proposal) => proposal.routeId)
}

/**
 * Point de départ et de retour des routes : Groupe RECA, 962 Rue Labelle, Saint-Jérôme, QC J7Z 5N1 (demande du
 * propriétaire, 12 octobre 2026). Géocodé par Mapbox (précision « toit ») le 12 octobre 2026 ; OpenStreetMap
 * place la même adresse à environ 25 m. Non modifiable dans l'outil.
 */
export const recaDepot = {
  label: 'Groupe RECA — 962 Rue Labelle, Saint-Jérôme',
  lat: 45.790931,
  lng: -74.007897,
} as const
